'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable, getFunctions } from 'firebase/functions';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  XMarkIcon,
  PaperAirplaneIcon,
  ArrowPathIcon,
  SparklesIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';

import {
  deriveDetailsForType,
  fieldsCoachShouldReview,
  hasMinimumDetails,
} from '@/lib/messagePrefill';
import { MESSAGE_TYPES } from '@/pages/messages-ai/messageTypes';
import SmsLengthIndicator from '@/components/messages/SmsLengthIndicator';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

/**
 * The "click-this-button-and-it's-mostly-done" composer.
 *
 * Replaces the multi-step ClubBroadcast wizard for the 80% case where:
 *   • The coach is acting on a SPECIFIC event (a past game / next practice / etc.)
 *   • Most of the form fields can be derived from that event's data
 *   • The only thing missing is whatever judgment the coach needs to add
 *     (highlights for a recap, reason for a cancellation, etc.)
 *
 * UX flow:
 *   1. Slide-over opens. Header shows event context (no form questions yet).
 *   2. We auto-call generateMessage with the prefill if enough data is there.
 *      If not, we show the missing-fields form first so the coach can fill them.
 *   3. The generated message previews (English; Spanish if generated).
 *   4. Coach can expand "Edit details" to tweak the auto-filled fields and re-generate.
 *   5. Coach hits "Send via SMS" — confirm dialog → sends via the per-team broadcast
 *      callable (sendCoachBroadcastSms). Push-first routing handles channel choice.
 *   6. Result panel replaces the send button with sent/failed counts + push/SMS breakdown.
 *
 * Reuses existing infra:
 *   • generateMessage (AI bilingual generation)
 *   • sendCoachBroadcastSms (per-team SMS fan-out, push-first via parentMessaging.js)
 *   • messageTypes.js (canonical field schemas)
 *   • messagePrefill.js (event → details derivation)
 *
 * Required props: { open, onClose, event, teamId, messageType }
 * Optional props: { defaultDetailOverrides } — start the form with extra prefill
 */
export default function NotifyComposerSlideOver({
  open,
  onClose,
  event,
  teamId,
  messageType,
  defaultDetailOverrides = {},
}) {
  // The MESSAGE_TYPES entry — gives us the field schema for the "Edit details" form
  const msgType = useMemo(
    () => MESSAGE_TYPES.find((t) => t.id === messageType) || null,
    [messageType]
  );

  // ── State ──
  const [details, setDetails] = useState(() =>
    deriveDetailsForType(messageType, event, defaultDetailOverrides)
  );
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null); // { bodyEn, bodyEs }
  // Snapshot of what was auto-derived from the event, so we can mark
  // each form field with a "✨ auto-filled" badge without confusing
  // pre-fill values vs coach-typed edits as the user types.
  const [autoFilled, setAutoFilled] = useState(() =>
    deriveDetailsForType(messageType, event, defaultDetailOverrides)
  );
  // Default the form to OPEN so the coach can see what was pre-filled
  // (and what's blank). Was previously hidden behind a click — that
  // made the smart-fill invisible and confusing.
  const [editingDetails, setEditingDetails] = useState(true);

  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null); // { attempted, sent, failed, sentPush, sentSms, status }

  // Re-derive details whenever the event/messageType changes (e.g. user
  // closes + reopens for a different event). Done on every open so the
  // coach always sees fresh data — opening a slide-over for a different
  // event shouldn't surface stale prefill from the previous one. The
  // autoFilled snapshot is also refreshed so the "✨ auto-filled" badges
  // stay accurate.
  useEffect(() => {
    if (!open) return;
    const derived = deriveDetailsForType(messageType, event, defaultDetailOverrides);
    setDetails(derived);
    setAutoFilled(derived);
    setEditingDetails(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, messageType, event?.id]);

  // Reset state on close so reopening for a different event doesn't show
  // the previous event's generated message.
  useEffect(() => {
    if (!open) {
      setResult(null);
      setSendResult(null);
      setEditingDetails(false);
      setGenerating(false);
      setSending(false);
    }
  }, [open]);

  // Body scroll lock — prevents the underlying page from scrolling while
  // the modal is open. Without this, the user sees the BROWSER scrollbar
  // (which controls page scroll) and assumes it scrolls the slide-over
  // content; instead the page moves silently behind the modal. Standard
  // React modal hygiene.
  useEffect(() => {
    if (!open) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, [open]);

  // ── Manual generate (no auto-draft) ──
  // Earlier iteration auto-fired generateMessage as soon as required
  // fields were present. That sounded smart but it actually fights
  // the coach: they want to add highlights / what-to-bring / etc.
  // BEFORE the AI commits to a draft. So now generation only fires when
  // they click "Draft Message" — same wait as the original wizard, but
  // with everything we know already pre-populated.
  const handleGenerate = async () => {
    if (!teamId || !msgType) return;
    if (generating) return;
    if (!hasMinimumDetails(messageType, details)) {
      toast.error('Please fill in the required fields first.');
      return;
    }
    setGenerating(true);
    try {
      const fn = httpsCallable(getFunctions(), 'generateMessage');
      const res = await fn({ teamId, messageType, details });
      setResult({
        bodyEn: res?.data?.bodyEn || '',
        bodyEs: res?.data?.bodyEs || '',
      });
    } catch (err) {
      console.error('[NotifyComposerSlideOver] generate error', err);
      toast.error(err?.message || 'Failed to draft message.');
    } finally {
      setGenerating(false);
    }
  };

  if (!open) return null;
  if (!msgType) {
    // Defensive — caller passed a messageType not in MESSAGE_TYPES
    return null;
  }

  const reviewFields = fieldsCoachShouldReview(messageType);
  const missingRequired = !hasMinimumDetails(messageType, details);

  // ── Send via SMS (per-team) ──
  // Calls the existing sendCoachBroadcastSms callable, which handles push-first
  // routing + STOP footer + per-team rate limit + per-parent SMS quota check.
  const handleSend = async () => {
    if (sending || !result?.bodyEn || !teamId) return;
    const ok = typeof window !== 'undefined' && window.confirm(
      'Send this message to all opted-in parents on this team?\n\n' +
      'Each parent gets the version matching their language preference. ' +
      'Parents with the app installed get a free push notification; ' +
      'others get an SMS. STOP/HELP footer is added automatically.'
    );
    if (!ok) return;

    setSending(true);
    try {
      const fn = httpsCallable(getFunctions(), 'sendCoachBroadcastSms');
      // Bilingual send: the server resolves each parent's language and
      // routes accordingly. Passing the AI-crafted bodyEs means Spanish
      // parents receive EXACTLY the Español preview the coach approved
      // on screen — without it the server machine-translates the
      // English, and preview ≠ wire (2026-07-24 messages audit).
      const res = await fn({
        teamId,
        seasonId: event?.seasonId || null,
        message: result.bodyEn,
        messageEs: result.bodyEs || null,
        urgent: false,
      });
      const data = res?.data || {};
      setSendResult(data);
      const { attempted = 0, sent = 0, failed = 0 } = data;
      if (attempted === 0) {
        toast('No reachable parents on this team.', { icon: 'ℹ️' });
      } else if (failed === 0) {
        toast.success(`Sent to ${sent} of ${attempted} parents`);
      } else if (sent === 0) {
        toast.error(`All ${attempted} sends failed`);
      } else {
        toast(`Sent ${sent} / ${attempted} (${failed} failed)`, { icon: '⚠️' });
      }
    } catch (err) {
      console.error('[NotifyComposerSlideOver] send error', err);
      const code = err?.code || '';
      if (code === 'functions/resource-exhausted') {
        toast.error(err?.message || 'Daily broadcast limit reached.');
      } else if (code === 'functions/permission-denied') {
        toast.error('Only team coaches can send a broadcast.');
      } else {
        toast.error(err?.message || 'Failed to send.');
      }
    } finally {
      setSending(false);
    }
  };

  const handleRegenerate = () => {
    // Coach tweaked details, hits Re-draft on the message preview.
    // Re-runs handleGenerate with the current details.
    handleGenerate();
  };

  return (
    // stopPropagation on the root prevents form clicks from bubbling up
    // to the EventCard's onClick handler (which would navigate to the
    // event detail page). The slide-over is fixed-positioned so it
    // visually escapes the card, but click events still travel through
    // React's component tree.
    <div
      className="fixed inset-0 z-50 flex justify-end"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={onClose} />

      {/* Slide-over panel — explicit `h-full` + flex column so the
          header/footer stay sticky and only the body scrolls. The
          earlier `overflow-y-auto` on the WHOLE panel scrolled the
          header out of view as the user scrolled down to read the
          drafted message. */}
      <div className="relative w-full sm:max-w-lg bg-white shadow-2xl flex flex-col h-full">

        {/* Pitch-dark header (sticky — shrink-0 keeps it from collapsing
            when the body grows). */}
        <div className="shrink-0 px-5 pt-5 pb-5 bg-[#0d1f14] relative overflow-hidden">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                'linear-gradient(to right, #b5f03d 1px, transparent 1px), linear-gradient(to bottom, #b5f03d 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }}
          />
          <div className="relative flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 text-xl">
                {msgType.icon}
              </div>
              <div className="min-w-0">
                <p
                  className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#b5f03d] mb-1"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {msgType.label}
                </p>
                <h2
                  className="uppercase text-white leading-[1.05] truncate"
                  style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 20 }}
                >
                  {event?.opponent
                    ? `vs ${event.opponent}`
                    : event?.title || 'Send to parents'}
                </h2>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex-shrink-0 p-1.5 rounded-lg border border-[#b5f03d]/40 bg-[#b5f03d]/10 hover:bg-[#b5f03d]/20 text-[#b5f03d] transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body — the only scrolling region. Header above + footer below
            stay fixed. flex-1 lets it absorb all remaining vertical space
            between the header and footer. */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">

          {/* If the auto-fill didn't have enough data (e.g. game with no score yet),
              show the missing fields form before generating. */}
          {missingRequired && !result && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-amber-800">
                Some details are missing
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Fill in the required fields below to generate the message.
              </p>
            </div>
          )}

          {/* Generated message preview */}
          {generating && (
            <div className="rounded-2xl border border-zinc-200 bg-white p-6 text-center">
              <ArrowPathIcon className="h-6 w-6 text-[#5a8a1f] animate-spin mx-auto mb-2" />
              <p
                className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                style={{ fontFamily: HEAD_FONT }}
              >
                Drafting your message…
              </p>
              <p className="text-xs text-zinc-500 mt-1">AI is writing both English + Spanish versions.</p>
            </div>
          )}

          {result && !generating && (
            <div className="space-y-3">
              <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
                  <div className="flex items-center gap-2">
                    <SparklesIcon className="h-4 w-4 text-[#5a8a1f]" />
                    <span
                      className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                      style={{ fontFamily: HEAD_FONT }}
                    >
                      Drafted message
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleRegenerate}
                    className="text-xs font-bold uppercase text-zinc-500 hover:text-[#5a8a1f] inline-flex items-center gap-1"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  >
                    <ArrowPathIcon className="h-3.5 w-3.5" />
                    Re-draft
                  </button>
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <span
                      className="text-[10px] font-bold uppercase tracking-[1.4px] text-zinc-400"
                      style={{ fontFamily: HEAD_FONT }}
                    >
                      🇺🇸 English
                    </span>
                    <SmsLengthIndicator body={result.bodyEn} />
                  </div>
                  <p className="text-sm text-[#0d1f14] whitespace-pre-wrap leading-relaxed">
                    {result.bodyEn}
                  </p>
                  {result.bodyEs && (
                    <>
                      <div className="my-4 border-t border-dashed border-zinc-200" />
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <span
                          className="text-[10px] font-bold uppercase tracking-[1.4px] text-zinc-400"
                          style={{ fontFamily: HEAD_FONT }}
                        >
                          🇲🇽 Español
                        </span>
                        <SmsLengthIndicator body={result.bodyEs} />
                      </div>
                      <p className="text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed">
                        {result.bodyEs}
                      </p>
                    </>
                  )}
                  {/* Hint when Spanish was skipped — server returned no
                      bodyEs because no team has Spanish-preference parents.
                      Surfaces the cost optimization to the coach so they
                      know it's intentional, not a bug. */}
                  {!result.bodyEs && (
                    <div className="mt-3 pt-3 border-t border-dashed border-zinc-200">
                      <p className="text-[11px] text-zinc-500 italic">
                        No Spanish-preference parents on this team — sending English only.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Details form — open by default so coach sees what was pre-filled.
              Each field carries a "✨ Auto-filled" badge when its value
              came from the event derivation (vs typed by the coach), so
              the smart-fill is visible — not invisible plumbing. */}
          {(() => {
            const filledFromPrefill = msgType.fields.filter(
              (f) => autoFilled[f.name] != null && String(autoFilled[f.name]).length > 0
            ).length;
            const totalFields = msgType.fields.length;
            return (
              <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
                <button
                  type="button"
                  onClick={() => setEditingDetails((v) => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left"
                >
                  <div className="flex items-center gap-2">
                    <SparklesIcon className="h-4 w-4 text-[#5a8a1f]" />
                    <span
                      className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                      style={{ fontFamily: HEAD_FONT }}
                    >
                      Details — {filledFromPrefill} of {totalFields} auto-filled
                    </span>
                  </div>
                  <ChevronDownIcon className={clsx('h-4 w-4 text-zinc-400 transition-transform', editingDetails && 'rotate-180')} />
                </button>
                {(editingDetails || missingRequired) && (
                  <div className="px-4 pb-4 space-y-3 border-t border-zinc-100 pt-3">
                    {msgType.fields.map((field) => {
                      const value = details[field.name] ?? '';
                      const isAutoFilled = autoFilled[field.name] != null
                        && String(autoFilled[field.name]).length > 0
                        && String(value) === String(autoFilled[field.name]);
                      const onChange = (v) => setDetails((d) => ({ ...d, [field.name]: v }));
                      const inputCls = clsx(
                        'w-full rounded-xl border bg-white px-3 py-2 text-sm text-[#0d1f14] placeholder-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 transition',
                        // Lime border when value matches what we auto-filled,
                        // zinc otherwise — so coach sees at a glance which
                        // fields are "we got this" vs "we need your input".
                        isAutoFilled ? 'border-[#b5f03d]/40 bg-[#b5f03d]/5' : 'border-zinc-300'
                      );
                      return (
                        <div key={field.name}>
                          <div className="flex items-center justify-between mb-1">
                            <label
                              className="text-[11px] font-bold uppercase tracking-[1.2px] text-zinc-500"
                              style={{ fontFamily: HEAD_FONT }}
                            >
                              {field.label}
                              {field.required && <span className="text-red-500 ml-0.5">*</span>}
                            </label>
                            {isAutoFilled && (
                              <span
                                className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide text-[#5a8a1f]"
                                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                                title="Auto-filled from this event"
                              >
                                ✨ Auto-filled
                              </span>
                            )}
                          </div>
                          {field.type === 'textarea' ? (
                            <textarea rows={3} className={inputCls} placeholder={field.placeholder || ''}
                              value={value} onChange={(e) => onChange(e.target.value)} />
                          ) : field.type === 'select' ? (
                            <select className={inputCls} value={value} onChange={(e) => onChange(e.target.value)}>
                              <option value="">Choose…</option>
                              {(field.options || []).map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : (
                            <input type={field.type === 'number' ? 'number' : (field.type || 'text')}
                              className={inputCls} placeholder={field.placeholder || ''}
                              value={value} onChange={(e) => onChange(e.target.value)} />
                          )}
                        </div>
                      );
                    })}
                    {/* Always-visible Draft button — coach reviews/edits
                        the prefilled fields, adds their context (highlights,
                        what-to-bring, etc.), then clicks Draft. AI fires.
                        After a successful draft, label flips to "Re-draft"
                        so the coach can iterate if they tweak the inputs. */}
                    <button
                      type="button"
                      onClick={handleGenerate}
                      disabled={!hasMinimumDetails(messageType, details) || generating}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50"
                      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                    >
                      {generating ? (
                        <>
                          <ArrowPathIcon className="h-4 w-4 animate-spin" />
                          Drafting…
                        </>
                      ) : (
                        <>
                          <SparklesIcon className="h-4 w-4" />
                          {result ? 'Re-draft message' : 'Draft message'}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Send result panel — appears after a successful (or partial) send */}
          {sendResult && (
            <div
              className={clsx(
                'rounded-2xl border p-5 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]',
                sendResult.status === 'sent' && 'border-emerald-200 bg-emerald-50',
                sendResult.status === 'partial' && 'border-amber-200 bg-amber-50',
                sendResult.status === 'failed' && 'border-red-200 bg-red-50',
                (!sendResult.status || sendResult.status === 'no_recipients') && 'border-zinc-200 bg-zinc-50',
              )}
            >
              <div className="flex items-start gap-3">
                <div className={clsx(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                  sendResult.status === 'sent' && 'bg-emerald-100 text-emerald-700',
                  sendResult.status === 'partial' && 'bg-amber-100 text-amber-700',
                  sendResult.status === 'failed' && 'bg-red-100 text-red-700',
                  (!sendResult.status || sendResult.status === 'no_recipients') && 'bg-zinc-200 text-zinc-600',
                )}>
                  {sendResult.status === 'sent' && <CheckCircleIcon className="h-5 w-5" />}
                  {sendResult.status === 'partial' && <ExclamationTriangleIcon className="h-5 w-5" />}
                  {sendResult.status === 'failed' && <ExclamationTriangleIcon className="h-5 w-5" />}
                  {(!sendResult.status || sendResult.status === 'no_recipients') && <PaperAirplaneIcon className="h-5 w-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#0d1f14]"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    {sendResult.status === 'sent' && 'Message sent'}
                    {sendResult.status === 'partial' && 'Partial delivery'}
                    {sendResult.status === 'failed' && 'Send failed'}
                    {(!sendResult.status || sendResult.status === 'no_recipients') && 'No recipients'}
                  </p>
                  <p className="text-sm text-[#0d1f14] mt-1">
                    {sendResult.attempted > 0
                      ? `${sendResult.sent} of ${sendResult.attempted} parent${sendResult.attempted === 1 ? '' : 's'} reached`
                      : 'No reachable parents on this team.'}
                    {sendResult.failed > 0 && (
                      <span className="text-red-600"> · {sendResult.failed} failed</span>
                    )}
                  </p>
                  {(sendResult.sentPush > 0 || sendResult.sentSms > 0) && (
                    <p className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                      {sendResult.sentPush > 0 && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-[#b5f03d]/15 text-[#3d6010] border border-[#b5f03d]/40 px-2 py-0.5 font-bold uppercase tracking-wide"
                          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                        >
                          🔔 {sendResult.sentPush} push
                        </span>
                      )}
                      {sendResult.sentSms > 0 && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-zinc-100 text-zinc-700 border border-zinc-200 px-2 py-0.5 font-bold uppercase tracking-wide"
                          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                        >
                          💬 {sendResult.sentSms} sms
                        </span>
                      )}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Footer — Send CTA. Sticky (shrink-0) so it stays visible
            while the body scrolls. Hidden after a successful send to
            prevent accidental double-fan-out. */}
        {!sendResult && (
          <div className="shrink-0 px-5 py-4 border-t border-zinc-100 bg-zinc-50">
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || !result?.bodyEn}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 py-3 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              {sending ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  On it…
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="h-4 w-4" />
                  Send to all parents
                </>
              )}
            </button>
            <p className="mt-2 text-[11px] text-center text-zinc-400">
              Push to app users (free) · SMS to everyone else
            </p>
          </div>
        )}
        {sendResult && (
          <div className="shrink-0 px-5 py-4 border-t border-zinc-100 bg-zinc-50">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex items-center justify-center rounded-xl border border-zinc-200 bg-white px-5 py-3 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
